import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Users, Sparkles, Search, Lock, Beaker, Brain } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";

export const About = () => {
  const { language } = useLanguage();
  const Arrow = language === 'he' ? ArrowLeft : ArrowRight;
  const isRTL = language === 'he';

  const content = {
    he: {
      title: "אודות M'ART",
      subtitle: "יוזמה עצמאית לקוסמטיקה טבעית | הוקמה 2024",
      intro: [
        "M'ART היא יוזמה עצמאית שהוקמה בשנת 2024, ללא מטרות רווח, ומתוך שליחות ברורה: הנגשת ידע, חינוך הקהילה ויצירת שיח פתוח סביב פיתוח קוסמטיקה טבעית איכותית.",
        "היוזמה אינה פועלת ממניעים מסחריים ואינה מתמקדת במכירה או ברווח. המהות של M'ART היא למידה מתמדת, ניסוי חוקר, שקיפות מלאה ושיתוף ידע ללא פשרות."
      ],
      researchTitle: "תחום העיסוק והמחקר",
      researchIntro: "M'ART מתמקדת במחקר ופיתוח של מוצרי קוסמטיקה מחומרים טבעיים בלבד. אנו מעמיקים בהבנת:",
      researchItems: [
        "פורמולציות לדאודורנטים טבעיים",
        "חומרים פעילים ממקור טבעי טהור",
        "תהליכי ערבוב, ייצוב ושיפור מרקם (Texturing)",
        "התאמה פיזיולוגית לשימוש יומיומי אפקטיבי"
      ],
      sodiumTitle: "סודיום ביקרבונט - חומר הליבה",
      sodiumText: "אחד מחומרי הליבה שנחקרו ביוזמה הוא הסודיום ביקרבונט, אשר הוכח כמרכיב הטבעי היעיל ביותר לנטרול ריחות, כאשר הוא משולב בפורמולה בצורה מדויקת ואחראית.",
      aiTitle: "פיתוח מבוסס בינה מלאכותית (AI)",
      aiText: "הידע והפורמולציות ב-M'ART נבנו בעזרת מערכות בינה מלאכותית מתקדמות, המשמשות ככלי מחקר וניתוח מובילים לדיוק יחסי חומרים, טכניקות ערבוב ובחינת יציבות.",
      aiQuote: "לאחר מאות ניסויים מעשיים, המטרה נותרה אחת: להבין מה באמת עובד, ולא מה משווק הכי טוב.",
      valuesTitle: "הגישה והערכים שלנו",
      valuesIntro: "אנו פועלים מתוך אמונה כי:",
      values: [
        { icon: Lock, title: "ידע פתוח", text: "ידע צריך להיות פתוח לכולם" },
        { icon: Search, title: "שקיפות מלאה", text: "תהליכי ייצור צריכים להיות שקופים לחלוטין" },
        { icon: Users, title: "קהילה חזקה", text: "קהילה חזקה צומחת משיתוף והעצמה, לא מהסתרה" }
      ],
      valuesConclusion: "האתר והתכנים המפורסמים בו מיועדים ללימוד חופשי, במטרה להעניק למשתמשים כלים לפיתוח עצמי, מודע ואחראי.",
      communityTitle: "קהילה ושיח פיתוח",
      communityText: "M'ART היא פלטפורמה פתוחה לדיון, שאלות, הצעות וביקורת מקצועית. אנו שואפים ליצור שיח מתמשך סביב פיתוח קוסמטיקה טבעית, ולעדכן את הקהילה בתובנות ובתוצאות מחקר, לטובת הכלל.",
      joinCommunity: "הצטרפו לקהילה",
      slogan: "ידע פתוח | חומרים טבעיים | פיתוח אחראי"
    },
    en: {
      title: "About M'ART",
      subtitle: "Independent Initiative for Natural Cosmetics | Founded 2024",
      intro: [
        "M'ART is an independent initiative founded in 2024, non-profit, with a clear mission: knowledge accessibility, community education, and creating open dialogue around quality natural cosmetics development.",
        "The initiative does not operate from commercial motives and does not focus on sales or profit. The essence of M'ART is continuous learning, exploratory experimentation, complete transparency, and uncompromising knowledge sharing."
      ],
      researchTitle: "Field of Work and Research",
      researchIntro: "M'ART focuses on research and development of cosmetic products from natural materials only. We delve into understanding:",
      researchItems: [
        "Natural deodorant formulations",
        "Active ingredients from pure natural sources",
        "Mixing, stabilization and texturing processes",
        "Physiological adaptation for effective daily use"
      ],
      sodiumTitle: "Sodium Bicarbonate - Core Ingredient",
      sodiumText: "One of the core materials researched in the initiative is sodium bicarbonate, which has been proven to be the most effective natural ingredient for odor neutralization when combined in the formula precisely and responsibly.",
      aiTitle: "AI-Based Development",
      aiText: "The knowledge and formulations at M'ART were built with the help of advanced artificial intelligence systems, serving as leading research and analysis tools for material ratio accuracy, mixing techniques, and stability testing.",
      aiQuote: "After hundreds of practical experiments, the goal remains one: to understand what really works, not what markets best.",
      valuesTitle: "Our Approach and Values",
      valuesIntro: "We operate from the belief that:",
      values: [
        { icon: Lock, title: "Open Knowledge", text: "Knowledge should be open to everyone" },
        { icon: Search, title: "Full Transparency", text: "Production processes should be completely transparent" },
        { icon: Users, title: "Strong Community", text: "A strong community grows from sharing and empowerment, not hiding" }
      ],
      valuesConclusion: "The website and published content are intended for free learning, aiming to provide users with tools for self-development, conscious and responsible.",
      communityTitle: "Community and Development Dialogue",
      communityText: "M'ART is an open platform for discussion, questions, suggestions, and professional criticism. We strive to create ongoing dialogue around natural cosmetics development, and update the community with insights and research results for the benefit of all.",
      joinCommunity: "Join the Community",
      slogan: "Open Knowledge | Natural Materials | Responsible Development"
    }
  };

  const t = content[language] || content.he;

  return (
    <div className="min-h-screen pt-20 pb-12">
      <SEO page="about" />
      
      {/* Header */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-[#4A5D43]/10 text-[#4A5D43] text-xs font-medium rounded-full mb-3 tracking-wider uppercase">
            {isRTL ? 'אודות' : 'About'}
          </span>
          <h1 
            data-testid="about-title"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-[#2C3329] mb-3"
          >
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-[#6B7266]">
            {t.subtitle}
          </p>
          <div className="decorative-line mt-4" />
        </div>
      </section>

      {/* Intro */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <div className="premium-card rounded-2xl p-6 sm:p-8 border-r-4 border-[#4A5D43]">
            {t.intro.map((paragraph, index) => (
              <p key={index} className={`text-[#2C3329] leading-relaxed ${index > 0 ? 'mt-4' : ''}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl text-[#2C3329] mb-4 flex items-center gap-3">
            <Beaker className="h-6 w-6 text-[#4A5D43]" />
            {t.researchTitle}
          </h2>
          
          <div className="premium-card rounded-2xl p-6 sm:p-8">
            <p className="text-[#6B7266] mb-4">{t.researchIntro}</p>
            
            <ul className="space-y-3 mb-6">
              {t.researchItems.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-[#4A5D43] mt-0.5 shrink-0" />
                  <span className="text-[#2C3329]">{item}</span>
                </li>
              ))}
            </ul>
            
            {/* Sodium Bicarbonate Highlight */}
            <div className="bg-gradient-to-br from-[#4A5D43] to-[#3A4A35] text-white rounded-xl p-5">
              <h3 className="font-medium text-lg mb-2">{t.sodiumTitle}</h3>
              <p className="text-white/90 text-sm leading-relaxed">{t.sodiumText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl text-[#2C3329] mb-4 flex items-center gap-3">
            <Brain className="h-6 w-6 text-[#4A5D43]" />
            {t.aiTitle}
          </h2>
          
          <div className="premium-card rounded-2xl p-6 sm:p-8">
            <p className="text-[#6B7266] mb-6">{t.aiText}</p>
            
            <div className="bg-[#F5F2EB] rounded-xl p-5 text-center">
              <p className="text-[#2C3329] font-medium italic">"{t.aiQuote}"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl text-[#2C3329] mb-4">{t.valuesTitle}</h2>
          <p className="text-[#6B7266] mb-6">{t.valuesIntro}</p>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {t.values.map((value, index) => (
              <div key={index} className="premium-card rounded-xl p-5 text-center">
                <div className="w-12 h-12 bg-[#4A5D43]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <value.icon className="h-6 w-6 text-[#4A5D43]" />
                </div>
                <h3 className="font-medium text-[#2C3329] mb-1">{value.title}</h3>
                <p className="text-sm text-[#6B7266]">{value.text}</p>
              </div>
            ))}
          </div>
          
          <div className="premium-card rounded-2xl p-6">
            <p className="text-[#6B7266]">{t.valuesConclusion}</p>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl text-[#2C3329] mb-4 flex items-center gap-3">
            <Users className="h-6 w-6 text-[#4A5D43]" />
            {t.communityTitle}
          </h2>
          
          <div className="premium-card rounded-2xl p-6 sm:p-8">
            <p className="text-[#6B7266] mb-6">{t.communityText}</p>
            
            <Link to="/community">
              <Button className="btn-primary rounded-full px-6">
                {t.joinCommunity}
                <Arrow className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Slogan */}
      <section className="px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[#2C3329] to-[#4A5D43] text-white rounded-2xl p-8 sm:p-12 text-center">
            <h3 className="font-serif text-3xl sm:text-4xl mb-4">M'ART</h3>
            <p className="text-lg sm:text-xl text-white/90 tracking-wider">{t.slogan}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
